// src/components/StepHeader.jsx
import React from "react";
import { ChevronLeft, Check, Shield } from "lucide-react";

export const STEPS = [
    { num: 1, label: "Privacy Notice" },
    { num: 2, label: "Purpose Selection" },
    { num: 3, label: "Review & Confirm" },
    { num: 4, label: "Completed" },
];

export function StepTracker({ currentStep }) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                background: "#ffffff",
                padding: "12px 24px",
                borderRadius: 12,
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.04)",
                marginBottom: 20,
            }}
        >
            {STEPS.map((step, idx) => {
                const isActive = step.num === currentStep;
                const isDone = step.num < currentStep;
                return (
                    <React.Fragment key={step.num}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "6px 12px",
                                borderRadius: 8,
                                background: isActive ? "#eff6ff" : "transparent",
                                transition: "all 0.15s ease",
                            }}
                        >
                            <div
                                style={{
                                    width: 26,
                                    height: 26,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 12,
                                    fontWeight: 700,
                                    background: isDone
                                        ? "#10b981"
                                        : isActive
                                            ? "#2563eb"
                                            : "#e2e8f0",
                                    color: isDone || isActive ? "#ffffff" : "#64748b",
                                    boxShadow: isActive
                                        ? "0 0 0 3px rgba(37, 99, 235, 0.2)"
                                        : isDone
                                            ? "0 2px 6px rgba(16, 185, 129, 0.25)"
                                            : "none",
                                    flexShrink: 0,
                                    transition: "all 0.2s ease",
                                }}
                            >
                                {isDone ? <Check size={15} strokeWidth={2.8} /> : step.num}
                            </div>

                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <span
                                    style={{
                                        fontSize: 13.5,
                                        fontWeight: isActive ? 700 : isDone ? 600 : 500,
                                        color: isActive
                                            ? "#1e40af"
                                            : isDone
                                                ? "#059669"
                                                : "#64748b",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {step.label}
                                </span>
                                {isActive && (
                                    <span
                                        style={{
                                            fontSize: 10,
                                            color: "#2563eb",
                                            fontWeight: 700,
                                            lineHeight: 1,
                                            marginTop: 1,
                                        }}
                                    >
                                        Current step
                                    </span>
                                )}
                            </div>
                        </div>

                        {idx < STEPS.length - 1 && (
                            <div
                                style={{
                                    flex: 1,
                                    height: 2,
                                    background: isDone
                                        ? "#10b981"
                                        : isActive
                                            ? "linear-gradient(90deg, #2563eb, #e2e8f0)"
                                            : "#e2e8f0",
                                    borderRadius: 99,
                                    margin: "0 12px",
                                }}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

export default function StepHeader({ currentStep, onBack }) {
    const handleBackClick = () => {
        if (typeof onBack === "function") {
            onBack();
        } else {
            window.history.back();
        }
    };

    return (
        <div
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                borderBottom: "1px solid #e2e8f0",
                padding: "16px 32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 16,
                boxShadow: "0 2px 10px rgba(15, 23, 42, 0.03)",
            }}
        >
            {/* Left side: Icon & Title */}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                    style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        background: "linear-gradient(135deg, #2563eb, #4f46e5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#ffffff",
                        boxShadow: "0 4px 12px rgba(37, 99, 235, 0.25)",
                        flexShrink: 0,
                    }}
                >
                    <Shield size={20} strokeWidth={2.2} />
                </div>
                <div>
                    <h1
                        style={{
                            fontSize: 18,
                            fontWeight: 800,
                            color: "#0f172a",
                            margin: 0,
                            lineHeight: 1.2,
                            letterSpacing: "-0.02em",
                        }}
                    >
                        Create Consent
                    </h1>
                    <p
                        style={{
                            fontSize: 12.5,
                            color: "#64748b",
                            margin: "2px 0 0",
                            fontWeight: 500,
                        }}
                    >
                        Follow the steps below to provide your consent
                    </p>
                </div>
            </div>

            {/* Right side: Back Button */}
            <button
                type="button"
                onClick={handleBackClick}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#ffffff",
                    border: "1.5px solid #cbd5e1",
                    borderRadius: 10,
                    padding: "8px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#334155",
                    cursor: "pointer",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.18s ease-in-out",
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#f1f5f9";
                    e.currentTarget.style.borderColor = "#94a3b8";
                    e.currentTarget.style.transform = "translateX(-2px)";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#ffffff";
                    e.currentTarget.style.borderColor = "#cbd5e1";
                    e.currentTarget.style.transform = "translateX(0)";
                }}
            >
                <ChevronLeft size={16} strokeWidth={2.2} />
                Back
            </button>
        </div>
    );
}
